import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { List, X, SignOut, CaretDown } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useAssessments } from "@/hooks/useAssessments";
import { AssessmentIcon } from "@/lib/icons";
import { CATEGORY_ORDER } from "@/lib/constants";

const FEATURE_LINKS = [
  { key: "assessments", label: "Assessments" },
  { key: "ai-coach", label: "AI Coach" },
  { key: "journal", label: "Journal" },
  { key: "inner-practices", label: "Inner Practices" },
  { key: "symbolic", label: "Pattern Mirror" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const [featOpen, setFeatOpen] = useState(false);
  const { user, logout } = useAuth();
  const { assessments } = useAssessments();
  const navigate = useNavigate();
  const ddRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ddRef.current && !ddRef.current.contains(e.target)) { setDropdown(false); setFeatOpen(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const byCat = {};
  assessments.forEach((a) => { (byCat[a.category] = byCat[a.category] || []).push(a); });
  const cats = CATEGORY_ORDER.filter((c) => byCat[c]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-slate-800/60 bg-slate-950/70 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
        <Logo />

        <div className="hidden items-center gap-7 lg:flex" ref={ddRef}>
          <div className="relative">
            <button onClick={() => setDropdown((v) => !v)} className="flex items-center gap-1.5 text-sm font-medium text-slate-300 transition-colors hover:text-teal-300" data-testid="nav-assessments">
              Assessments <CaretDown size={14} className={`transition-transform ${dropdown ? "rotate-180" : ""}`} />
            </button>
            {dropdown && (
              <div className="absolute left-1/2 top-full mt-3 w-[640px] -translate-x-1/2 rounded-2xl border border-slate-800 bg-slate-900 p-5 shadow-2xl" data-testid="nav-assessments-menu">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  {cats.map((cat) => (
                    <div key={cat} className="py-1">
                      <p className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-teal-500">{cat}</p>
                      {byCat[cat].map((a) => (
                        <Link key={a.slug} to={`/a/${a.slug}`} onClick={() => setDropdown(false)} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-slate-800">
                          <AssessmentIcon name={a.icon} size={16} weight="duotone" style={{ color: a.color }} />
                          <span className="text-sm text-slate-300">{a.short}</span>
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Link to="/#all" className="text-sm font-medium text-slate-400 hover:text-teal-300">All Tests</Link>
          <div className="relative">
            <button onClick={() => setFeatOpen((v) => !v)} className="flex items-center gap-1.5 text-sm font-medium text-slate-300 transition-colors hover:text-teal-300" data-testid="nav-features">
              Features <CaretDown size={14} className={`transition-transform ${featOpen ? "rotate-180" : ""}`} />
            </button>
            {featOpen && (
              <div className="absolute left-1/2 top-full mt-3 w-56 -translate-x-1/2 rounded-2xl border border-slate-800 bg-slate-900 p-2 shadow-2xl" data-testid="nav-features-menu">
                {FEATURE_LINKS.map((f) => (
                  <Link key={f.key} to={`/features/${f.key}`} onClick={() => setFeatOpen(false)} className="block rounded-lg px-3 py-2 text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-teal-200" data-testid={`nav-feature-${f.key}`}>{f.label}</Link>
                ))}
                <Link to="/compare" onClick={() => setFeatOpen(false)} className="mt-1 block rounded-lg border-t border-slate-800 px-3 py-2 text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-teal-200" data-testid="nav-compare">Compare results</Link>
              </div>
            )}
          </div>
          <Link to="/#how" className="text-sm font-medium text-slate-400 hover:text-teal-300">How It Works</Link>
          <Link to="/about" className="text-sm font-medium text-slate-400 hover:text-teal-300" data-testid="nav-about">Meet Avnni</Link>
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <>
              <Link to="/dashboard" className="rounded-full bg-teal-500 px-5 py-2 text-sm font-medium text-slate-950 transition-all hover:bg-teal-400 hover:shadow-[0_0_24px_rgba(20,184,166,0.4)]" data-testid="nav-dashboard-btn">Dashboard</Link>
              <button onClick={handleLogout} className="text-slate-400 hover:text-slate-200" data-testid="nav-logout-btn" title="Sign out"><SignOut size={20} weight="duotone" /></button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-slate-300 hover:text-white" data-testid="nav-login-btn">Log in</Link>
              <Link to="/#all" className="rounded-full bg-teal-500 px-5 py-2 text-sm font-medium text-slate-950 transition-all hover:bg-teal-400 hover:shadow-[0_0_24px_rgba(20,184,166,0.4)]" data-testid="nav-start-btn">Take a Free Test</Link>
            </>
          )}
        </div>

        <button onClick={() => setOpen((v) => !v)} className="text-slate-200 lg:hidden" data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </nav>

      {open && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-slate-800/60 bg-slate-950/95 px-5 py-4 lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-1">
            <p className="px-1 py-1 text-[11px] font-bold uppercase tracking-wider text-teal-500">Assessments</p>
            {assessments.map((a) => (
              <Link key={a.slug} to={`/a/${a.slug}`} onClick={() => setOpen(false)} className="flex items-center gap-2.5 rounded-lg px-2 py-2">
                <AssessmentIcon name={a.icon} size={16} weight="duotone" style={{ color: a.color }} />
                <span className="text-sm text-slate-300">{a.short}</span>
              </Link>
            ))}
            <div className="my-2 h-px bg-slate-800" />
            <p className="px-1 py-1 text-[11px] font-bold uppercase tracking-wider text-teal-500">Features</p>
            {FEATURE_LINKS.map((f) => (
              <Link key={f.key} to={`/features/${f.key}`} onClick={() => setOpen(false)} className="px-2 py-2 text-sm text-slate-300" data-testid={`nav-feature-mobile-${f.key}`}>{f.label}</Link>
            ))}
            <Link to="/compare" onClick={() => setOpen(false)} className="px-2 py-2 text-sm text-slate-300" data-testid="nav-compare-mobile">Compare results</Link>
            <div className="my-2 h-px bg-slate-800" />
            <Link to="/about" onClick={() => setOpen(false)} className="px-2 py-2 text-sm font-medium text-slate-300" data-testid="nav-about-mobile">Meet Avnni</Link>
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="rounded-full bg-teal-500 px-5 py-2.5 text-center text-sm font-medium text-slate-950">Dashboard</Link>
                <button onClick={handleLogout} className="mt-2 text-left text-sm text-slate-400">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-300 px-2 py-2">Log in</Link>
                <Link to="/#all" onClick={() => setOpen(false)} className="rounded-full bg-teal-500 px-5 py-2.5 text-center text-sm font-medium text-slate-950">Take a Free Test</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
